import React from 'react';
import { Modal } from 'antd';

/**
 * ตัวช่วยฟอร์มแนบรูป (ยื่นเคลม / ลงทะเบียน / แก้ไข) + การตรวจรูปซ้ำจาก admin-api
 */

// code ที่ admin-api ส่งมาใน result.code เมื่อตรวจรูปซ้ำ
export const IMAGE_CHECK_CODE = {
  DUPLICATE: 'IMAGE_DUPLICATE', // ไฟล์ซ้ำเป๊ะ บันทึกไม่ได้
  SIMILAR: 'IMAGE_SIMILAR', // รูปคล้าย ยืนยันแล้วส่งใหม่ได้
};

/**
 * แปลง URL รูปเดิมเป็น fileList ของ antd Upload
 * ไม่มี URL -> [] เพื่อให้ required ทำงานและปุ่ม Upload แสดง
 * @param {string} url
 */
export const toUploadFileList = (url) =>
  url ? [{ uid: url, url, name: url.split('/').pop() }] : [];

/**
 * แนบเฉพาะไฟล์ที่เลือกใหม่ (รูปเดิมที่มีแค่ url ไม่ต้องส่ง backend จะคงรูปเดิมไว้)
 * @param {FormData} formData
 * @param {string} key ชื่อ field ที่ backend รับ
 * @param {Array|Object} fileListOrFile fileList ของ antd หรือ file item ตัวเดียว
 */
export const appendUploadFile = (formData, key, fileListOrFile) => {
  const file = Array.isArray(fileListOrFile) ? fileListOrFile[0] : fileListOrFile;
  if (file && file.originFileObj) {
    formData.append(key, file.originFileObj);
  }
};

/**
 * แนบ flag ยืนยันรูปคล้าย (ส่งเมื่อผู้ใช้กดยืนยันจาก confirmSimilarImage แล้ว)
 */
export const appendConfirmSimilarImage = (formData, confirmSimilarImage) => {
  if (confirmSimilarImage) {
    formData.append('confirmSimilarImage', 'true');
  }
};

// ฟิลด์เตือนใน result.item ของ response ที่บันทึกสำเร็จแต่งานรองล้ม
export const SAVE_WARNING_KEY = {
  IMAGE_HASH: 'imageHashErrorMsg', // บันทึก hash รูปสำหรับตรวจซ้ำไม่สำเร็จ
  MEMBER_ORDER: 'memberOrderErrorMsg', // สร้าง member order / คูปองไม่สำเร็จ
};

/**
 * บันทึกสำเร็จแต่มีคำเตือน -> แสดง Modal.warning ให้ admin เห็นก่อนไปต่อ
 * @param {Object} response response จาก POST
 * @param {string[]} keys ฟิลด์ใน SAVE_WARNING_KEY ที่ต้องตรวจ
 * @param {() => void} [onOk] ทำต่อหลังกดตกลง (เช่น เปลี่ยนหน้า)
 * @returns {boolean} true = มีคำเตือนและแสดงแล้ว
 */
export const showSaveWarnings = (response, keys, onOk) => {
  const item = response?.result?.item || {};
  const messages = keys.map((key) => item[key]).filter(Boolean);
  if (messages.length === 0) return false;
  Modal.warning({
    title: response?.message || 'บันทึกสำเร็จ',
    content: (
      <div>
        {messages.map((message) => (
          <p key={message} style={{ margin: '4px 0' }}>
            {message}
          </p>
        ))}
      </div>
    ),
    okText: 'ตกลง',
    onOk,
  });
  return true;
};

export const isSimilarImageError = (error) => error?.result?.code === IMAGE_CHECK_CODE.SIMILAR;

/**
 * ครอบ POST ที่ส่งรูป: ถ้าเป็นรูปคล้าย ไม่ให้ global error modal (MutationCache) แสดง
 * เพราะ onError ของ hook จะถามยืนยันแทน
 * @param {() => Promise<any>} postFn
 */
export const postWithImageCheck = async (postFn) => {
  try {
    return await postFn();
  } catch (error) {
    if (isSimilarImageError(error) && error && typeof error === 'object') {
      error.disabledThrow = true;
    }
    throw error;
  }
};

/**
 * ถ้า error เป็นรูปคล้าย: เปิด modal ให้ยืนยัน แล้วเรียก onConfirm (ส่งใหม่พร้อม confirmSimilarImage)
 * @returns {boolean} true = จัดการแล้ว
 */
export const confirmSimilarImage = (error, onConfirm) => {
  if (!isSimilarImageError(error)) return false;
  const matches = error?.result?.matches || [];
  Modal.confirm({
    title: 'พบรูปภาพคล้ายกับที่เคยใช้ในระบบ',
    width: 520,
    content: (
      <div>
        <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
          {matches.map((match) => (
            <li key={`${match.imageType}-${match.refCode}-${match.imageUrl}`}>
              {match.imageTypeName} คล้ายกับ{match.moduleName} {match.refCode}{' '}
              {match.imageUrl && (
                <a href={match.imageUrl} target="_blank" rel="noreferrer">
                  (ดูรูป)
                </a>
              )}
            </li>
          ))}
        </ul>
        <div>ตรวจสอบแล้วต้องการบันทึกต่อหรือไม่?</div>
      </div>
    ),
    okText: 'ยืนยันบันทึก',
    cancelText: 'ยกเลิก',
    onOk: onConfirm,
  });
  return true;
};
